import * as React from 'react';
import {
  ISlidingDateRangeTypeOption,
  getSlidingRange,
} from '../../../../data/providers/settings/dates/slidingDateRangeProvider';
import {
  IRange,
  IScoreboardSettings as ILegacyScoreboardSettings,
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
  ISelectOption,
} from '../../../../data/types';
import { setScoreboardSettings } from '../../../../data/writers/settings/scoreboardSettings';
import {
  DEFAULT_BRANDING,
  DEFAULT_DISPLAY_CONFIG,
  DEFAULT_THEME,
  IScoreboardBranding,
  IScoreboardDisplayConfig,
  IScoreboardSettings,
  IScoreboardTheme,
  ISplitViewConfig,
  SplitColumnFilterType,
  migrateLegacySettings,
} from '../types';

type SegmentType = 'none' | 'team' | 'group';

const deriveSplitViewSegmentType = (
  splitView: ISplitViewConfig
): SegmentType => {
  if (!splitView?.enabled) return 'none';
  return splitView.leftColumn.filterType;
};

const toExtendedSettings = (
  settings: ILegacyScoreboardSettings
): IScoreboardSettings | null => {
  if (!settings) return null;
  if (
    'version' in settings &&
    (settings as IScoreboardSettings).version === 2
  ) {
    return settings as IScoreboardSettings;
  }
  return migrateLegacySettings(settings);
};

interface UseSettingsHandlersOptions {
  savedSettings: ILegacyScoreboardSettings;
  setCurrentSettings: React.Dispatch<
    React.SetStateAction<IScoreboardSettings | null>
  >;
  setSegmentType: React.Dispatch<React.SetStateAction<SegmentType>>;
  setSelectedTeam: React.Dispatch<React.SetStateAction<ISelectOption>>;
  setSelectedGroup: React.Dispatch<React.SetStateAction<ISelectOption>>;
  setSelectedLeftColumn: React.Dispatch<React.SetStateAction<ISelectOption>>;
  setSelectedRightColumn: React.Dispatch<React.SetStateAction<ISelectOption>>;
  setSelectedLeftColumnSecondaryFilter: React.Dispatch<React.SetStateAction<ISelectOption>>;
  setSelectedRightColumnSecondaryFilter: React.Dispatch<React.SetStateAction<ISelectOption>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentSettings: IScoreboardSettings | null;
}

interface UseSettingsHandlersResult {
  handleTeamChange: (team: ISelectOption) => void;
  handleGroupChange: (group: ISelectOption) => void;
  handleSegmentTypeChange: (details: { value: string }) => void;
  handleLeftColumnChange: (option: ISelectOption) => void;
  handleRightColumnChange: (option: ISelectOption) => void;
  handleLeftColumnTagsChange: (tags: ISelectableTag[]) => void;
  handleRightColumnTagsChange: (tags: ISelectableTag[]) => void;
  handleLeftColumnSecondaryFilterChange: (option: ISelectOption) => void;
  handleRightColumnSecondaryFilterChange: (option: ISelectOption) => void;
  handleTestTypeChange: (testType: ISelectableTestType) => void;
  handleMetricChange: (metric: ISelectableMetric) => void;
  handleDateChange: (
    dateRangeType: ISlidingDateRangeTypeOption,
    dateRange: IRange
  ) => void;
  handleTagsChange: (tags: ISelectableTag[]) => void;
  updateTheme: (updates: Partial<IScoreboardTheme>) => void;
  updateDisplay: (updates: Partial<IScoreboardDisplayConfig>) => void;
  updateBranding: (updates: Partial<IScoreboardBranding>) => void;
  resetAppearanceDefaults: () => void;
  applyChanges: () => Promise<void>;
  handleClose: () => void;
  isSaving: boolean;
  saveError: string | null;
  clearSaveError: () => void;
}

export const useSettingsHandlers = ({
  savedSettings,
  setCurrentSettings,
  setSegmentType,
  setSelectedTeam,
  setSelectedGroup,
  setSelectedLeftColumn,
  setSelectedRightColumn,
  setSelectedLeftColumnSecondaryFilter,
  setSelectedRightColumnSecondaryFilter,
  setOpen,
  currentSettings,
}: UseSettingsHandlersOptions): UseSettingsHandlersResult => {
  const currentSettingsRef = React.useRef(currentSettings);
  currentSettingsRef.current = currentSettings;
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const handleLeftColumnTagsChange = (tags: ISelectableTag[]): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          splitView: {
            ...prev.splitView,
            leftColumn: {
              ...prev.splitView.leftColumn,
              tags,
            },
          },
        }
    );
  };

  const handleRightColumnTagsChange = (tags: ISelectableTag[]): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          splitView: {
            ...prev.splitView,
            rightColumn: {
              ...prev.splitView.rightColumn,
              tags,
            },
          },
        }
    );
  };

  const handleLeftColumnSecondaryFilterChange = (option: ISelectOption): void => {
    setSelectedLeftColumnSecondaryFilter(option);
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          splitView: {
            ...prev.splitView,
            leftColumn: {
              ...prev.splitView.leftColumn,
              secondaryFilterIds: option ? [option.value] : [],
            },
          },
        }
    );
  };

  const handleRightColumnSecondaryFilterChange = (option: ISelectOption): void => {
    setSelectedRightColumnSecondaryFilter(option);
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          splitView: {
            ...prev.splitView,
            rightColumn: {
              ...prev.splitView.rightColumn,
              secondaryFilterIds: option ? [option.value] : [],
            },
          },
        }
    );
  };

  const handleTeamChange = (team: ISelectOption): void => {
    setSelectedTeam(team);
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null => {
        if (!prev) return null;
        // In 'none' mode, clear groups when team changes
        // In split mode, preserve groups as they are independent filters
        const shouldClearGroups = !prev.splitView?.enabled;
        return {
          ...prev,
          filteredTeams: team ? [team.value] : [],
          filteredGroups: shouldClearGroups ? [] : prev.filteredGroups,
        };
      }
    );
    // Only clear selected group in 'none' mode
    if (!currentSettingsRef.current?.splitView?.enabled) {
      setSelectedGroup(null);
    }
  };

  const handleGroupChange = (group: ISelectOption): void => {
    setSelectedGroup(group);
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          filteredGroups: group ? [group.value] : [],
        }
    );
  };

  const handleSegmentTypeChange = (details: { value: string }): void => {
    const newType = details.value as SegmentType;
    setSegmentType(newType);
    setSelectedTeam(null);
    setSelectedGroup(null);
    setSelectedLeftColumn(null);
    setSelectedRightColumn(null);
    setSelectedLeftColumnSecondaryFilter(null);
    setSelectedRightColumnSecondaryFilter(null);

    const filterType: SplitColumnFilterType =
      newType === 'none' ? 'team' : newType;

    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          filteredTeams: [],
          filteredGroups: [],
          tags: [],
          splitView: {
            enabled: newType !== 'none',
            leftColumn: { title: '', filterType, filteredIds: [], tags: [], secondaryFilterIds: [] },
            rightColumn: { title: '', filterType, filteredIds: [], tags: [], secondaryFilterIds: [] },
          },
        }
    );
  };

  const handleLeftColumnChange = (option: ISelectOption): void => {
    setSelectedLeftColumn(option);
    setSelectedLeftColumnSecondaryFilter(null);
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          splitView: {
            ...prev.splitView,
            leftColumn: {
              ...prev.splitView.leftColumn,
              title: option?.label ?? '',
              filteredIds: option ? [option.value] : [],
              secondaryFilterIds: [],
              tags: [],
            },
          },
        }
    );
  };

  const handleRightColumnChange = (option: ISelectOption): void => {
    setSelectedRightColumn(option);
    setSelectedRightColumnSecondaryFilter(null);
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          splitView: {
            ...prev.splitView,
            rightColumn: {
              ...prev.splitView.rightColumn,
              title: option?.label ?? '',
              filteredIds: option ? [option.value] : [],
              secondaryFilterIds: [],
              tags: [],
            },
          },
        }
    );
  };

  const handleTestTypeChange = (testType: ISelectableTestType): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && { ...prev, testType }
    );
  };

  const handleMetricChange = (metric: ISelectableMetric): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          metrics: {
            ...prev.metrics,
            [prev.testType.value]: metric,
          },
        }
    );
  };

  const handleDateChange = (
    dateRangeType: ISlidingDateRangeTypeOption,
    dateRange: IRange
  ): void => {
    const newRange =
      dateRangeType.value === 'custom'
        ? dateRange
        : getSlidingRange(dateRangeType);
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          dateRangeType,
          dateRange: newRange,
        }
    );
  };

  const handleTagsChange = (tags: ISelectableTag[]): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          tags,
        }
    );
  };

  const updateTheme = (updates: Partial<IScoreboardTheme>): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          theme: { ...prev.theme, ...updates },
        }
    );
  };

  const updateDisplay = (updates: Partial<IScoreboardDisplayConfig>): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          display: { ...prev.display, ...updates },
        }
    );
  };

  const updateBranding = (updates: Partial<IScoreboardBranding>): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          branding: { ...prev.branding, ...updates },
        }
    );
  };

  const resetAppearanceDefaults = (): void => {
    setCurrentSettings(
      (prev: IScoreboardSettings | null): IScoreboardSettings | null =>
        prev && {
          ...prev,
          theme: DEFAULT_THEME,
          display: DEFAULT_DISPLAY_CONFIG,
          branding: DEFAULT_BRANDING,
        }
    );
  };

  const applyChanges = async (): Promise<void> => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await setScoreboardSettings(currentSettingsRef.current);
      setOpen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ScoreboardSettings] Error saving settings:', error);
      setSaveError('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const clearSaveError = (): void => {
    setSaveError(null);
  };

  const handleClose = (): void => {
    // Revert to the last saved settings on close without saving
    const reverted = toExtendedSettings(savedSettings);
    setCurrentSettings(reverted);
    setSegmentType(deriveSplitViewSegmentType(reverted?.splitView));
    setOpen(false);
  };

  return {
    handleTeamChange,
    handleGroupChange,
    handleSegmentTypeChange,
    handleLeftColumnChange,
    handleRightColumnChange,
    handleLeftColumnTagsChange,
    handleRightColumnTagsChange,
    handleLeftColumnSecondaryFilterChange,
    handleRightColumnSecondaryFilterChange,
    handleTestTypeChange,
    handleMetricChange,
    handleDateChange,
    handleTagsChange,
    updateTheme,
    updateDisplay,
    updateBranding,
    resetAppearanceDefaults,
    applyChanges,
    handleClose,
    isSaving,
    saveError,
    clearSaveError,
  };
};

export { toExtendedSettings, deriveSplitViewSegmentType };
export type { SegmentType };
